import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Image, DollarSign, Package, ShoppingBag, RefreshCw, Truck } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = "https://backend.dlapakistan.com/api";
const API_ENDPOINTS = {
  DLA_SHOP_PRODUCTS: `${API_BASE_URL}/dla-shop/products/`,
  DLA_SHOP_CATEGORIES: `${API_BASE_URL}/dla-shop/categories/`,
};

const AllProductsPage = () => {
  const navigate = useNavigate();
  
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  const emerald600 = "#059669";

  // Fetch all products from backend
  const fetchAllProducts = async () => {
    const loading = isRefreshing ? setIsRefreshing : setIsLoading;
    loading(true);
    setError(null);
    try {
      console.log('Fetching all products from:', API_ENDPOINTS.DLA_SHOP_PRODUCTS);
      
      const response = await axios.get(API_ENDPOINTS.DLA_SHOP_PRODUCTS);
      console.log('Products response:', response.data);
      
      let products = [];
      if (Array.isArray(response.data)) {
        products = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        products = response.data.results;
      } else if (response.data && Array.isArray(response.data.data)) {
        products = response.data.data;
      } else {
        console.error('Unexpected response format:', response.data);
        products = [];
      }
      
      console.log('Fetched products:', products.length);
      setAllProducts(products);
    } catch (err) {
      console.error("Error fetching products:", err);
      if (err.response) {
        console.error("Response data:", err.response.data);
        console.error("Response status:", err.response.status);
      }
      setError("Failed to load products. Please try again.");
      setAllProducts([]);
    } finally {
      loading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchAllProducts();
  }, []);

  const handleBackToShop = () => {
    navigate('/dla-shop');
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Recently added";
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return "Recently added";
    }
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return `$${isNaN(numPrice) ? '0.00' : numPrice.toFixed(2)}`;
  };

  const calculateTotalWithShipping = (product) => {
    const price = parseFloat(product.price) || 0;
    const shipping = parseFloat(product.shipping_amount) || 0;
    return (price + shipping).toFixed(2);
  };

  const handleViewProduct = (product) => {
    console.log('View product:', product);
    const totalWithShipping = calculateTotalWithShipping(product);
    alert(`Viewing product: ${product.title}
Price: ${formatPrice(product.price)}
Shipping: ${formatPrice(product.shipping_amount || 0)}
Total: ${formatPrice(totalWithShipping)}
Category: ${product.category_title || 'Uncategorized'}`);
  };

  const handleCategoryClick = (categoryId, e) => {
    e.stopPropagation();
    if (categoryId) {
      navigate(`/dla-shop/categories/${categoryId}/products`);
    }
  };

  const handleRefreshProducts = () => {
    setIsRefreshing(true);
    fetchAllProducts();
  };

  // Calculate statistics
  const totalProducts = allProducts.length;
  const totalValue = allProducts.reduce((sum, product) => {
    const price = parseFloat(product.price) || 0;
    const shipping = parseFloat(product.shipping_amount) || 0;
    const totalPerUnit = price + shipping;
    const amount = parseInt(product.amount) || 0;
    return sum + (totalPerUnit * amount);
  }, 0);
  
  const totalShippingValue = allProducts.reduce((sum, product) => {
    const shipping = parseFloat(product.shipping_amount) || 0;
    const amount = parseInt(product.amount) || 0;
    return sum + (shipping * amount);
  }, 0);
  
  const uniqueCategoryIds = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
  const totalCategories = uniqueCategoryIds.length;

  // Sort products by newest first
  const sortedProducts = [...allProducts].sort((a, b) => {
    const aDate = new Date(a.created_at || 0);
    const bDate = new Date(b.created_at || 0);
    return bDate - aDate;
  });

  if (isLoading) {
    return (
      <div className="flex flex-col mt-20 min-h-screen w-full">
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            {/* Back Button */}
            <button
              onClick={handleBackToShop}
              className="flex items-center gap-2 text-gray-600 hover:text-emerald-600 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Shop</span>
            </button>
            
            {/* Page Title */}
            <h1 className="text-3xl font-bold text-gray-900">
              All Products
            </h1>
            <p className="text-gray-600 mt-1">
              Browse all products from all categories in the DLA Shop
            </p>
            
            {/* Error Message */}
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600">{error}</p>
              </div>
            )}
            
            {/* Stats and Refresh Button */}
            <div className="flex flex-wrap items-center gap-4 mt-4">
              <div className="flex items-center gap-3">
                <div className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                  {totalProducts} Products
                </div>
                <div className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {totalCategories} Categories
                </div>
                <div className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  Total Value: ${totalValue.toFixed(2)}
                </div>
                <div className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                  Shipping: ${totalShippingValue.toFixed(2)}
                </div>
              </div>
              <button
                onClick={handleRefreshProducts}
                className="flex items-center gap-2 px-4 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-full text-sm font-medium transition-colors"
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {sortedProducts.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl hover:border-emerald-400 transition-colors">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-50 flex items-center justify-center">
              <ShoppingBag className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-lg mb-2 font-medium">
              No products found in the shop
            </p>
            <p className="text-gray-400 text-sm mb-6">
              Add products to categories to see them here
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/dla-shop')}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
              >
                <ShoppingBag className="w-5 h-5" />
                Go to Categories
              </button>
              <button
                onClick={handleRefreshProducts}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fadeIn">
            {sortedProducts.map((product, index) => {
              const totalWithShipping = calculateTotalWithShipping(product);
              
              return (
                <div 
                  key={`${product.id}-${index}`} 
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all duration-300 hover:border-emerald-100 group cursor-pointer"
                  onClick={() => handleViewProduct(product)}
                >
                  {/* Product Image */}
                  <div className="h-48 w-full relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Image className="w-16 h-16 opacity-40" />
                      </div>
                    )}
                    
                    {/* Total Price Badge */}
                    <div className="absolute top-4 right-4 bg-purple-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                      Total: ${totalWithShipping}
                    </div>
                    
                    {/* Category Badge */}
                    {product.category_title && (
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg">
                        {product.category_title}
                      </div>
                    )}
                    
                    {/* Amount Badge */}
                    <div className="absolute bottom-4 right-4 bg-blue-500 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg">
                      {product.amount || 0} units
                    </div>
                    
                    {/* Published Status */}
                    <div className="absolute bottom-4 left-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${product.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {product.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Product Title */}
                    <div className="mb-3">
                      <h2 className="text-xl font-bold text-gray-900 truncate pr-4 group-hover:text-emerald-600 transition-colors mb-2">
                        {product.title || 'Untitled Product'}
                      </h2>
                      {product.category_title && (
                        <button
                          onClick={(e) => handleCategoryClick(product.category, e)}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg text-sm font-medium transition-colors"
                        >
                          {product.category_title}
                        </button>
                      )}
                    </div>

                    {/* Product Description - RENDER HTML PROPERLY */}
                    <div className="mb-4 max-h-32 overflow-y-auto">
                      <div 
                        className="text-gray-600 text-sm prose prose-sm max-w-none line-clamp-3"
                        dangerouslySetInnerHTML={{ __html: product.description || '' }}
                      />
                      {!product.description && (
                        <p className="text-gray-400 italic">No description available</p>
                      )}
                    </div>

                    {/* Price Breakdown */}
                    <div className="mb-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-gray-700 font-medium">Price:</span>
                        </div>
                        <span className="text-emerald-600 font-semibold">
                          ${parseFloat(product.price || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center space-x-2">
                          <Truck className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-gray-700 font-medium">Shipping:</span>
                        </div>
                        <span className="text-blue-600 font-semibold">
                          ${parseFloat(product.shipping_amount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 border-dashed">
                        <div className="flex items-center space-x-2">
                          <Package className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-700 font-medium">Total:</span>
                        </div>
                        <span className="text-purple-600 font-bold text-lg">
                          ${totalWithShipping}
                        </span>
                      </div>
                    </div>

                    {/* Product Details */}
                    <div className="mt-2 pt-4 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between text-sm text-gray-500 gap-4">
                      <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
                        {product.created_at && (
                          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg">
                            <span className="font-medium text-gray-700">Added:</span> 
                            <span className="text-gray-600">{formatDate(product.created_at)}</span>
                          </div>
                        )}
                      </div>
                      <div className="text-emerald-600 font-medium bg-emerald-50 px-4 py-1.5 rounded-full border border-emerald-100">
                        #{index + 1}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Styles for rendered HTML content */
        .prose {
          max-width: 100%;
          word-wrap: break-word;
        }
        
        .prose p {
          margin: 0.5rem 0;
        }
        
        .prose p:first-child {
          margin-top: 0;
        }
        
        .prose p:last-child {
          margin-bottom: 0;
        }
        
        .prose ul, .prose ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
          list-style-position: outside;
        }
        
        .prose ul {
          list-style-type: disc;
        }
        
        .prose ol {
          list-style-type: decimal;
        }
        
        .prose li {
          margin: 0.25rem 0;
        }
        
        .prose strong, .prose b {
          font-weight: 700;
        }
        
        .prose em, .prose i {
          font-style: italic;
        }
        
        .prose h1, .prose h2, .prose h3, .prose h4, .prose h5, .prose h6 {
          font-weight: 600;
          margin: 0.75rem 0 0.5rem 0;
          line-height: 1.3;
        }
        
        .prose h1 { 
          font-size: 1.5rem; 
        }
        
        .prose h2 { 
          font-size: 1.25rem; 
        }
        
        .prose h3 { 
          font-size: 1.125rem; 
        }
        
        .prose h4 { 
          font-size: 1rem; 
        }
        
        .prose table {
          width: 100%;
          border-collapse: collapse;
          margin: 0.75rem 0;
          font-size: 0.875rem;
        }
        
        .prose td, .prose th {
          border: 1px solid #e5e7eb;
          padding: 0.5rem;
          text-align: left;
        }
        
        .prose th {
          background-color: #f9fafb;
          font-weight: 600;
        }
        
        .prose blockquote {
          border-left: 4px solid #e5e7eb;
          padding-left: 1rem;
          margin: 0.75rem 0;
          font-style: italic;
          color: #4b5563;
        }
        
        .prose a {
          color: #059669;
          text-decoration: underline;
          text-decoration-thickness: 1px;
          text-underline-offset: 2px;
        }
        
        .prose a:hover {
          color: #047857;
        }
        
        .prose hr {
          border: 0;
          border-top: 1px solid #e5e7eb;
          margin: 1rem 0;
        }
        
        .prose code {
          background-color: #f3f4f6;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
          font-family: monospace;
        }
        
        .prose pre {
          background-color: #f3f4f6;
          padding: 0.75rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 0.75rem 0;
        }
        
        .prose pre code {
          background-color: transparent;
          padding: 0;
          border-radius: 0;
        }
        
        .prose img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 0.75rem 0;
        }
        
        .prose .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Scrollbar styling for description area */
        .overflow-y-auto::-webkit-scrollbar {
          width: 6px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default AllProductsPage;